import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import socket from "../socketInstance";
import { postApi } from "../helpers/apiStructure";
import { notifications as notificationsRoute } from "../const";

const MAX_NOTIFICATIONS = 20;

const getNotificationText = (notification) => {
  if (notification?.chatGroup) {
    return notification?.groupName
      ? `${notification.senderDisplayName} in ${notification.groupName}`
      : `${notification.senderDisplayName} sent a group message`;
  }

  return `${notification.senderDisplayName} sent a message`;
};

const getNotificationPreview = (notification) => {
  if (notification?.content) {
    return notification.content;
  }

  switch (notification?.messageType) {
    case "image":
      return "Sent an image";
    case "sticker":
      return "Sent a sticker";
    case "video":
      return "Sent a video";
    case "audio":
      return "Sent an audio message";
    case "file":
      return "Sent a file";
    default:
      return "New message";
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const persistReadState = async (notificationIds = []) => {
    if (!localStorage.getItem("x-auth-token")) {
      return;
    }

    const baseUrl = `${window.location.protocol}//${window.location.host}`;

    try {
      await postApi(`${baseUrl}/api/notifications/read`, "post", {
        notificationIds,
      });
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  useEffect(() => {
    const loadNotifications = async () => {
      if (!localStorage.getItem("x-auth-token")) {
        return;
      }

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const response = await postApi(`${baseUrl}/api/notifications`, "post", {
        page: 1,
      });

      if (response?.status === 1) {
        setNotifications(response?.data || []);
      }
    };

    loadNotifications();
  }, []);

  useEffect(() => {
    const handleNewMessage = (message) => {
      const newNotification = {
        id: message?.id || `${message?.chatGroup || message?.sender}-${message?.createdAt || Date.now()}`,
        sender: message?.sender,
        recipient: message?.recipient,
        chatGroup: message?.chatGroup,
        groupName: message?.groupName || "",
        senderDisplayName: message?.senderDisplayName || message?.senderName || "User",
        messageType: message?.messageType || "text",
        content: message?.content || "",
        createdAt: message?.createdAt || new Date().toISOString(),
        isRead: false,
      };

      setNotifications((currentNotifications) => {
        const updatedNotifications = [
          newNotification,
          ...currentNotifications.filter(
            (notification) => notification?.id !== newNotification.id
          ),
        ].slice(0, MAX_NOTIFICATIONS);
        return updatedNotifications;
      });
    };

    const listener = (data) => {
      try {
        const parsedData = JSON.parse(data);
        handleNewMessage(parsedData);
      } catch (error) {
        console.error("Failed to parse notification data:", error);
      }
    };

    socket.on("new_message", listener);
    return () => socket.off("new_message", listener);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification?.isRead).length,
    [notifications]
  );

  const markAllAsRead = () => {
    const unreadIds = notifications
      .filter((notification) => !notification?.isRead)
      .map((notification) => notification?.id)
      .filter(Boolean);

    if (unreadIds.length === 0) {
      return;
    }

    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) => ({
        ...notification,
        isRead: true,
      }))
    );

    persistReadState(unreadIds);
  };

  const openNotification = (notification) => {
    if (notification?.id && !notification?.isRead) {
      setNotifications((currentNotifications) =>
        currentNotifications.map((currentNotification) =>
          currentNotification?.id === notification.id
            ? { ...currentNotification, isRead: true }
            : currentNotification
        )
      );
      persistReadState([notification.id]);
    }

    const userSlug = localStorage.getItem("slug");
    if (notification?.chatGroup) {
      if (userSlug === "admin" || userSlug === "branch_leader") {
        navigate(
          `/admin-group-chat?groupId=${notification.chatGroup}&name=${encodeURIComponent(
            notification.groupName || "Group"
          )}&time=${notification.createdAt || ""}`
        );
        return;
      }

      navigate(
        `/chat?group=true&groupId=${notification.chatGroup}&name=${encodeURIComponent(
          notification.groupName || "Group"
        )}&time=${notification.createdAt || ""}`
      );
      return;
    }

    const senderId =
      typeof notification?.sender === "string"
        ? notification.sender
        : notification?.sender?.id || notification?.sender?._id;

    if (!senderId) {
      return;
    }

    if (userSlug === "admin" || userSlug === "branch_leader") {
      navigate(
        `/admin-chat?id=${senderId}&name=${encodeURIComponent(
          notification.senderDisplayName || "User"
        )}&time=${notification.createdAt || ""}`
      );
      return;
    }

    navigate(
      `/chat?id=${senderId}&name=${encodeURIComponent(
        notification.senderDisplayName || "User"
      )}&time=${notification.createdAt || ""}`
    );
  };

  return (
    <Dropdown
      align="end"
      onToggle={(isOpen) => isOpen && markAllAsRead()}
      className="notification-dropdown"
    >
      <Dropdown.Toggle
        as={Button}
        variant="light"
        className="notification-bell-button position-relative border-0"
      >
        <i className="bi bi-bell-fill"></i>
        {unreadCount > 0 && (
          <Badge bg="danger" pill className="notification-bell-badge">
            {unreadCount}
          </Badge>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="notification-dropdown-menu">
        <div className="notification-dropdown-header">
          <strong>Notifications</strong>
        </div>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <Dropdown.Item
              key={notification.id}
              className={`notification-item ${notification?.isRead ? "" : "notification-item-unread"}`}
              onClick={() => openNotification(notification)}
            >
              <div className="notification-title">
                {getNotificationText(notification)}
              </div>
              <div className="notification-preview">
                {getNotificationPreview(notification)}
              </div>
              <div className="notification-time">
                {moment(notification?.createdAt).format("DD MMM, hh:mm A")}
              </div>
            </Dropdown.Item>
          ))
        ) : (
          <div className="notification-empty">No notifications yet.</div>
        )}
        <div className="notification-dropdown-footer">
          <button
            type="button"
            className="notification-view-all"
            onClick={() => navigate(notificationsRoute)}
          >
            View all notifications
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationBell;
